/**
 * FolderFormDialog — create or edit a folder.
 * Combines name / description / icon / color in one dialog.
 *
 * Props:
 *  - open:           bool
 *  - onOpenChange:   (open) => void
 *  - mode:           'create' | 'edit'
 *  - parentName:     string | null  (for create — shows "Inside <parent>")
 *  - initial:        folder | null  (for edit)
 *  - onSubmit:       (payload) => Promise<void>
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { FOLDER_ICONS, FOLDER_ICON_KEYS, FOLDER_COLORS, resolveIcon } from './folderMeta';

export function FolderFormDialog({ open, onOpenChange, mode, parentName, initial, onSubmit }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('folder');
  const [color, setColor] = useState('#64748B');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setName(initial.name || '');
      setDescription(initial.description || '');
      setIcon(initial.icon || 'folder');
      setColor(initial.color || '#64748B');
    } else {
      setName('');
      setDescription('');
      setIcon('folder');
      setColor('#64748B');
    }
  }, [open, mode, initial]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmed,
        description: description.trim() || null,
        icon,
        color,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const PreviewIcon = resolveIcon(icon);

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="folder-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit folder' : 'New folder'}
          </DialogTitle>
          {mode === 'create' && (
            <DialogDescription>
              {parentName ? `Inside "${parentName}"` : 'At the root level'}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3 p-3 rounded-md bg-muted/40 border">
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center"
              style={{ backgroundColor: `${color}1F` }}
            >
              <PreviewIcon className="w-5 h-5" style={{ color }} />
            </div>
            <div className="text-sm">
              <div className="font-medium">{name || 'Folder name'}</div>
              {description && <div className="text-muted-foreground line-clamp-1">{description}</div>}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="folder-name">Name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={64}
              autoFocus
              placeholder="e.g. Sales Onboarding"
              data-testid="folder-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="folder-description">Description (optional)</Label>
            <Textarea
              id="folder-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={280}
              rows={2}
              placeholder="What's this folder for? Shown as a banner to learners."
              data-testid="folder-description-input"
            />
            <div className="text-xs text-muted-foreground text-right">{description.length}/280</div>
          </div>

          <div className="space-y-1.5">
            <Label>Icon</Label>
            <div className="grid grid-cols-10 gap-1.5" data-testid="folder-icon-picker">
              {FOLDER_ICON_KEYS.map(key => {
                const I = FOLDER_ICONS[key];
                const active = key === icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setIcon(key)}
                    className={`p-2 rounded-md border transition-colors
                      ${active ? 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/10' : 'border-transparent hover:bg-muted'}`}
                    data-testid={`folder-icon-${key}`}
                    title={key}
                  >
                    <I className="w-4 h-4 mx-auto" style={{ color: active ? color : undefined }} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2" data-testid="folder-color-picker">
              {FOLDER_COLORS.map(c => {
                const active = c.hex.toLowerCase() === color.toLowerCase();
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setColor(c.hex)}
                    className={`w-7 h-7 rounded-full border-2 transition-transform
                      ${active ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c.hex }}
                    aria-label={c.key}
                    data-testid={`folder-color-${c.key}`}
                    title={c.key}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="folder-form-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !name.trim()} data-testid="folder-form-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === 'edit' ? 'Save changes' : 'Create folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
